/**
 * ArrivalNotificationsSettings.jsx — "Customer Notifications" card:
 * 1) arrival texts/emails when the crew checks in on site
 * 2) completion summary email (photos + rating link) when a job completes
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Switch } from './ui/switch';
import { Bell, Loader2, MailCheck } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

const ToggleRow = ({ title, onText, offText, enabled, saving, onToggle, testId }) => (
  <div className="flex items-center justify-between rounded-lg border p-4">
    <div>
      <div className="font-medium">{title}</div>
      <div className="text-sm text-slate-500 mt-0.5">{enabled ? onText : offText}</div>
    </div>
    <Switch checked={enabled} onCheckedChange={onToggle} disabled={saving} data-testid={testId} />
  </div>
);

export const ArrivalNotificationsSettings = () => {
  const [arrival, setArrival] = useState(false);
  const [completion, setCompletion] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/company/arrival-notifications`, { withCredentials: true }),
      axios.get(`${API}/company/completion-email`, { withCredentials: true }),
    ])
      .then(([a, c]) => { setArrival(!!a.data.enabled); setCompletion(!!c.data.enabled); })
      .catch(() => toast.error('Could not load customer notification settings'))
      .finally(() => setLoading(false));
  }, []);

  const save = async (endpoint, next, setter, onMsg, offMsg) => {
    setSaving(true);
    try {
      await axios.put(`${API}/company/${endpoint}`, { enabled: next }, { withCredentials: true });
      setter(next);
      toast.success(next ? onMsg : offMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="arrival-notifications-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Bell className="w-5 h-5 text-amber-500" />
          Customer Notifications
        </CardTitle>
        <CardDescription>
          Automatic messages that keep your customers in the loop — both use the
          phone and email on the customer&apos;s record and include their private
          portal link.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        ) : (
          <>
            <ToggleRow
              title="Notify customers on crew arrival"
              onText="On — customers get a text/email the moment their job is started on site"
              offText="Off — no automatic arrival messages"
              enabled={arrival}
              saving={saving}
              onToggle={(v) => save('arrival-notifications', v, setArrival,
                'Customers will now be notified when your crew arrives on site',
                'Arrival notifications turned off')}
              testId="arrival-notifications-toggle"
            />
            <ToggleRow
              title="Completion summary email"
              onText="On — every completed job sends the customer a wrap-up email with photos and a rating link"
              offText="Off — only jobs with the per-job 'email on complete' checkbox send a summary"
              enabled={completion}
              saving={saving}
              onToggle={(v) => save('completion-email', v, setCompletion,
                'Customers will now get a wrap-up email when their job completes',
                'Completion summary emails turned off')}
              testId="completion-email-toggle"
            />
            <p className="text-xs text-slate-400 flex items-center gap-1.5">
              <MailCheck className="w-3.5 h-3.5" />
              The completion email includes job details, up to date photos (links valid 7 days) and a one-tap 👍/👎 rating.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ArrivalNotificationsSettings;
